/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import Popover from '@material-ui/core/Popover';
import {makeStyles} from '@material-ui/core/styles';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {dlsColor, dlsShadow, dlsZIndex} from '../../../theme/theme';
import {MenuItem, MenuItems} from '../../../types/components';
import NavigationList from './NavigationList';

/**
 * Defines the props for the current-navigation submenu flyout.
 */
interface NavigationFlyoutProps {
  activeLabel?: string;
  anchorEl: HTMLElement | null;
  currentPage: string;
  onClose?: () => void;
  onSetPage(page: string, index: number, isSecondaryIndex?: boolean, item?: MenuItem, isMainMenu?: boolean): void;
  parentIndex: number;
  secondaryMenuItems: MenuItems;
  visible: boolean;
}

const useStyles = makeStyles(() => ({
  // the theme pins every popover at `4001 !important`, which leaves the
  // flyout under any page scrim drawn higher - the time mode panel's among
  // them. Its own layer keeps the main menu's submenus above those, and the
  // doubled class outranks the pin whichever sheet lands first ...
  root : {
    pointerEvents : 'none',
    '&&'          : {
      zIndex : `${dlsZIndex('navigationFlyout')} !important`
    }
  },
  paper : {
    border        : `1px solid ${dlsColor('grey5')}`,
    borderRadius  : 8,
    boxShadow     : dlsShadow(3.2),
    marginLeft    : 8,
    marginTop     : 8,
    overflow      : 'visible',
    pointerEvents : 'auto'
  },
  content : {
    minWidth : 224,
    padding  : '4px 0'
  },
  header : {
    alignItems     : 'center',
    color          : dlsColor('lightTeal'),
    display        : 'flex',
    fontFamily     : 'Inter',
    fontSize       : 14,
    fontWeight     : 500,
    justifyContent : 'space-between',
    lineHeight     : '20px',
    padding        : '4px 8px 8px'
  },
  body : {
    padding : '0 16px 0 0'
  }
}));

/**
 * Renders the current-theme submenu flyout for collapsed and large submenus.
 *
 * @param props Defines the flyout anchor, items, and visibility.
 * @returns The submenu flyout popover when it should be shown.
 */
const NavigationFlyout = ({
  activeLabel,
  anchorEl,
  currentPage,
  onClose,
  onSetPage,
  parentIndex,
  secondaryMenuItems,
  visible
}: NavigationFlyoutProps) => {
  const classes = useStyles();
  const handleSetPage = useCallback((page: string, index: number, isSecondaryIndex?: boolean) => {
    onClose?.();
    onSetPage(page, parentIndex, isSecondaryIndex, undefined, false);
  }, [onClose, onSetPage, parentIndex]);

  if (!visible) {
    return null;
  }

  return (
    <Popover open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical   : 'top',
        horizontal : 'right'
      }}
      transformOrigin={{
        vertical   : 'top',
        horizontal : 'left'
      }}
      classes={{
        root  : classes.root,
        paper : classes.paper
      }}>
      <div className={classes.content}
        data-navigation-flyout='true'>
        <div className={classes.header}>
          <span>{activeLabel}</span>
          <ChevronRightIcon htmlColor={dlsColor('lightTeal')} style={{transform: 'rotate(90deg)'}} />
        </div>
        <div className={classes.body}>
          <NavigationList items={secondaryMenuItems}
            selectedIndex={-1}
            currentPage={currentPage}
            onSetPage={handleSetPage}
            nested
            isFlyout />
        </div>
      </div>
    </Popover>
  );
};

export default memo(NavigationFlyout);
